import React from 'react';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import Section from '../shared/Section';
import SectionHeading from '../shared/SectionHeading';
import ForestRoundedIcon from '@mui/icons-material/ForestRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ElectricBoltRoundedIcon from '@mui/icons-material/ElectricBoltRounded';

const sustainabilityPillars = [
  {
    icon: ForestRoundedIcon,
    title: 'Traceable sourcing',
    description:
      'Digital supplier passports log provenance, yields, and surplus so we choose partners who grow with the same zero-waste intent.',
  },
  {
    icon: InsightsRoundedIcon,
    title: 'Data-backed forecasting',
    description:
      'Predictive planning aligns production volumes with verified demand and surfaces optimisation ideas in real time.',
  },
  {
    icon: ElectricBoltRoundedIcon,
    title: 'Smart logistics',
    description:
      'Dynamic routing and reusable assets move meals efficiently, diverting excess to recovery partners before it becomes waste.',
  },
];

const sustainabilityDescription =
  'FoodFe pairs culinary craft with automation. Signals from sourcing, kitchens, and diners feed one platform so organisations can prove — and improve — their sustainability impact with every order.';

export const Sustain = () => {
  const sustainImage = `${process.env.PUBLIC_URL}/assets/images/DK5.jpg`;
  return (
    <Section
      id='sustain'
      background='linear-gradient(120deg, rgba(46, 189, 133, 0.12) 0%, rgba(245, 247, 251, 1) 100%)'
    >
      <Grid container spacing={{ xs: 6, md: 10 }} alignItems='center'>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: 'relative',
              width: { xs: '92%', md: '85%' },
              maxWidth: 500,
              mx: 'auto',
              borderRadius: 36,
              overflow: 'hidden',
              p: { xs: 2, md: 2.5 },
              background: 'linear-gradient(140deg, rgba(46,189,133,0.18) 0%, rgba(245,247,251,0.9) 100%)',
              boxShadow: '0px 28px 60px rgba(31, 36, 51, 0.16)',
            }}
          >
            <Box
              component='img'
              src={sustainImage}
              alt='Sustainable kitchen operations'
              sx={{
                width: '100%',
                height: { xs: 260, sm: 320, md: 360 },
                objectFit: 'cover',
                display: 'block',
                borderRadius: 28,
                boxShadow: '0px 18px 32px rgba(31, 36, 51, 0.14)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                border: '1px solid rgba(255,255,255,0.3)',
                pointerEvents: 'none',
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={4}>
            <SectionHeading
              align='left'
              eyebrow='Sustainability'
              title='Automation keeps sustainability at the heart of service'
              description={sustainabilityDescription}
            />
            <Stack spacing={3}>
              {sustainabilityPillars.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <Paper
                    key={pillar.title}
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 18,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      border: '1px solid rgba(47, 58, 114, 0.08)',
                      transition: 'transform 200ms ease, box-shadow 220ms ease',
                      boxShadow: '0px 14px 24px rgba(31, 36, 51, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0px 24px 45px rgba(31, 36, 51, 0.16)',
                      },
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 2.5, sm: 3 }}
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Box
                        sx={{
                          flexBasis: { xs: '100%', sm: '40%' },
                          maxWidth: { sm: '42%' },
                          width: '100%',
                          display: 'flex',
                          justifyContent: { xs: 'center', sm: 'flex-start' },
                          minWidth: 0,
                        }}
                      >
                        <Stack
                          direction='row'
                          spacing={1.5}
                          alignItems='center'
                          sx={{
                            width: '100%',
                            borderRadius: 999,
                            border: '1px solid rgba(46, 189, 133, 0.45)',
                            backgroundColor: 'rgba(46, 189, 133, 0.08)',
                            px: 2,
                            py: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            component={Icon}
                            sx={{ fontSize: '2rem', color: 'success.main', flexShrink: 0 }}
                          />
                          <Typography
                            variant='subtitle1'
                            component='span'
                            sx={{
                              fontWeight: 600,
                              fontSize: '1.3rem',
                              lineHeight: 1.4,
                              color: 'success.main',
                              whiteSpace: 'normal',
                              minWidth: 0,
                            }}
                          >
                            {pillar.title}
                          </Typography>
                        </Stack>
                      </Box>
                      <Typography
                        variant='body1'
                        sx={{
                          flexBasis: { xs: '100%', sm: '50%' },
                          textAlign: { xs: 'center', sm: 'left' },
                          minWidth: 0,
                          alignSelf: 'center',
                        }}
                      >
                        {pillar.description}
                      </Typography>
                    </Stack>
                 </Paper>
                );
              })}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Section>
  );
};
